import { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import App from "./app";

// ----------------------------------------------------------------------

if (import.meta.env.VITE_E2E !== "true") {
  Sentry.init({
    dsn: "https://dc6133e0f4b3449d1e7ff287719f3059@o4511494826229760.ingest.de.sentry.io/4511494829834320",

    environment: import.meta.env.VITE_APP_VERSION,
    release: import.meta.env.VITE_APP_VERSION || "local",

    sendDefaultPii: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    tracesSampleRate: 0.1,

    // Normál sessionökből csak kevés replay
    replaysSessionSampleRate: 0.02,

    // Hibánál mindig legyen replay
    replaysOnErrorSampleRate: 1.0,
  });
}

const PRELOAD_RELOAD_KEY = "foggosport_preload_reload_at";
const PRELOAD_RELOAD_COOLDOWN_MS = 10_000;

if (import.meta.env.PROD) {
  window.addEventListener("vite:preloadError", (event) => {
    const preloadEvent = event as Event & { payload?: unknown };

    Sentry.captureException(
      preloadEvent.payload ?? new Error("Vite dynamic import preload failed"),
      { tags: { source: "vite.preloadError" } }
    );

    let shouldReload = false;

    try {
      const lastReloadAt = Number(
        window.sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0
      );
      shouldReload = Date.now() - lastReloadAt > PRELOAD_RELOAD_COOLDOWN_MS;

      if (shouldReload) {
        window.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
      }
    } catch (error) {
      console.error("Unable to persist preload recovery state", error);
    }

    if (shouldReload) {
      event.preventDefault();
      window.location.reload();
    }
  });
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container #app not found");
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <HelmetProvider>
    <BrowserRouter>
      <Suspense>
        <App />
      </Suspense>
    </BrowserRouter>
  </HelmetProvider>
);
